import { createTheme } from '@mui/material';

const borderRadius = 4;
// Colors from the app palette (BT-10832) — keep in step with
// common/src/theme/themes/nutail.jsx.
const Palette_Primary = '#175fc9';
const Palette_Primary_Tint = '#e8f0fe';
const Palette_AI = '#7c3aed';
const Palette_AI_Tint = '#ede9fe';
const Palette_Success = '#16a34a';
const Palette_Success_Tint = '#dcfce7';
const Palette_Warning = '#d97706';
const Palette_Warning_Tint = '#fef3c7';
const Palette_Danger = '#dc2626';
const Palette_Danger_Tint = '#fee2e2';
const Palette_Ink = '#111827';
const Palette_Text_Secondary = '#6b7280';
const Palette_Muted = '#9ca3af';
const Palette_Border = '#e0e0e0';
const Palette_Subtle_Surface = '#f5f5f5';
const Palette_Surface = '#ffffff';
const Palette_Backdrop = '#f5f8fc';

// Compliance status: each keeps its hue, in the palette's tone.
const Color_Nephritis = Palette_Success;
// Not found uses the darker Secondary grey so the chip's white text reads (4.8:1,
// against 2.5:1 on Muted) — BT-10846.
const Color_Concrete = Palette_Text_Secondary;
const Color_Pumpkin = Palette_Danger;
const Color_Sunflower = Palette_Warning;

// eslint-disable-next-line import/no-mutable-exports
let nutailTheme = createTheme({
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius,
        },
        sizeSmall: {
          fontSize: 11,
        },
        sizeMedium: {
          fontSize: 13,
        },
        sizeLarge: {
          fontSize: 16,
        },

      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          // fontSize: 13,
          textTransform: 'none',
          borderRadius,
          variants: [
            {
              props: { variant: 'outlined' },
              style: {
                // Experimental: Try White instead of transparent for outlined buttons
                backgroundColor: '#ffffff',
              },
            },
          ],
        },
        sizeSmall: {
          fontSize: 11,
        },
        sizeMedium: {
          fontSize: 13,
        },
        sizeLarge: {
          fontSize: 16,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontSize: 13,
          textTransform: 'none',
          padding: '6px 16px',
          borderRadius,
        },
        sizeSmall: {
          fontSize: 11,
        },
        sizeMedium: {
          fontSize: 13,
        },
        sizeLarge: {
          fontSize: 16,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
        },
      },
    },
  },
  palette: {
    // MUI's core roles, on the palette.
    primary: { main: Palette_Primary, contrastText: '#fff' },
    success: { main: Palette_Success, contrastText: '#fff' },
    warning: { main: Palette_Warning, contrastText: '#fff' },
    error: { main: Palette_Danger, contrastText: '#fff' },
    info: { main: Palette_Primary, contrastText: '#fff' },
    text: { primary: Palette_Ink, secondary: Palette_Text_Secondary },
    divider: Palette_Border,
    background: { default: Palette_Surface, paper: Palette_Surface },
    // The app palette's role tokens, named as in common's theme so screens read
    // the same paths in both apps (PAL 9.12, BT-10862).
    activity_info: { main: Palette_Primary, light: Palette_Primary_Tint },
    activity_success: { main: Palette_Success, light: Palette_Success_Tint },
    activity_warning: { main: Palette_Warning, light: Palette_Warning_Tint },
    activity_danger: { main: Palette_Danger, light: Palette_Danger_Tint },
    activity_ai: { main: Palette_AI, light: Palette_AI_Tint },
    activity_neutral: { main: Palette_Text_Secondary, light: Palette_Subtle_Surface },
    activity_muted: { main: Palette_Muted },
    card_header: { main: Palette_Ink },
    modern_backdrop: { main: Palette_Backdrop },
    completed: {
      main: Color_Nephritis,
    },
    in_progress: {
      main: Color_Sunflower,
    },
    not_started: {
      main: Color_Pumpkin,
    },
    not_found: {
      main: Color_Concrete,
    },
  },
  typography: {
    fontFamily: [
      'Roboto-Regular',
      'Roboto',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    fontSize: 14,
  },
});

nutailTheme = createTheme(nutailTheme, {
  palette: {
    completed: nutailTheme.palette.augmentColor({
      color: {
        main: Color_Nephritis,
        contrastText: '#fff',
      },
      name: 'completed',
    }),
    in_progress: nutailTheme.palette.augmentColor({
      color: {
        main: Color_Sunflower,
        contrastText: '#fff',
      },
      name: 'in_progress',
    }),
    not_started: nutailTheme.palette.augmentColor({
      color: {
        main: Color_Pumpkin,
        contrastText: '#fff',
      },
      name: 'not_started',
    }),
    not_found: nutailTheme.palette.augmentColor({
      color: {
        main: Color_Concrete,
        contrastText: '#fff',
      },
      name: 'not_found',
    }),
  },
});


/**
 * A palette colour by path, e.g. `paletteColor('activity_success.light')`. Most of
 * superadmin renders outside a ThemeProvider, so screens read colours from this
 * theme directly rather than through `theme.palette` or sx palette paths.
 */
export const paletteColor = (path) => path.split('.').reduce((node, key) => node?.[key], nutailTheme.palette);

export default nutailTheme;
